import { CloudinaryTransformOptions } from '#app/utilities/cloudinaryTransforms/types.js';
import { getAssetUrl } from './getAssetUrl.js';
import { VideoProps } from './types.js';
import { Container, Sizer, SizerImage, StyledVideo } from './Video.styles.js';
import { encodeBase64 } from '@contra/utilities/encodeBase64';
import { forwardRef, useMemo } from 'react';

const BASE_TRANSFORM_OPTIONS: CloudinaryTransformOptions = {
  crop: 'fill',
  quality: 'auto:good',
};

export const Video = forwardRef<HTMLVideoElement, VideoProps>(
  (
    {
      assetType,
      height,
      layout,
      objectFit,
      objectPosition,
      transformOptions,
      uid,
      width,
      ...videoProps
    },
    ref,
  ) => {
    const { mpegUrl, posterUrl, webmUrl } = useMemo(() => {
      const combinedTransformOptions: CloudinaryTransformOptions = {
        ...BASE_TRANSFORM_OPTIONS,
        ...transformOptions,
        height: height * 2,
        width: width * 2,
      };

      return {
        mpegUrl: getAssetUrl({
          assetType,
          transformOptions: {
            ...combinedTransformOptions,
            format: 'mp4',
          },
          uid,
        }),
        posterUrl: getAssetUrl({
          assetType,
          transformOptions: {
            ...combinedTransformOptions,
            format: 'avif',
            height,
            page: assetType === 'image' ? 1 : undefined,
            quality: 'auto:eco',
            width,
          },
          uid,
        }),
        webmUrl: getAssetUrl({
          assetType,
          transformOptions: {
            ...combinedTransformOptions,
            format: 'webm',
          },
          uid,
        }),
      };
    }, [assetType, height, transformOptions, uid, width]);

    return (
      <Container
        $height={height}
        $layout={layout}
        $width={width}
      >
        {(layout === 'intrinsic' || layout === 'responsive') &&
        height !== undefined &&
        width !== undefined ? (
          <Sizer
            $height={height}
            $width={width}
            layout={layout}
          >
            {layout === 'intrinsic' ? (
              <SizerImage
                alt=""
                aria-hidden
                role="presentation"
                src={`data:image/svg+xml;base64,${encodeBase64(
                  `<svg width="${width}" height="${height}" xmlns="http://www.w3.org/2000/svg" version="1.1" />`,
                )}`}
              />
            ) : null}
          </Sizer>
        ) : null}
        <StyledVideo
          $objectFit={objectFit}
          $objectPosition={objectPosition}
          poster={posterUrl}
          ref={ref}
          {...videoProps}
        >
          <source
            src={webmUrl}
            type="video/webm"
          />
          <source
            src={mpegUrl}
            type="video/mp4"
          />
          Your browser does not support HTML video. Here is{' '}
          <a
            href={mpegUrl}
            rel="noreferrer"
            target="_blank"
          >
            a link to the video
          </a>{' '}
          instead.
        </StyledVideo>
      </Container>
    );
  },
);
